/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/passwordSettings.scss';

import React, {FocusEventHandler, MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import {ChangeFunction, Loader} from '@dvrd/dvr-controls';

import {PasswordErrors} from '../../../util/interfaces';

import PasswordStrength from '../../password/passwordStrength';

import {BolmateButton, BolmatePasswordInput} from '../../controls';

import BolmateSwitch from '../../controls/switch/bolmateSwitch';

import InfoBanner from '../../misc/infoBanner';

interface Props {
    onChange: (key: 'new' | 'confirm' | 'pass') => ChangeFunction;
    onSubmit: MouseEventHandler;
    onResetError: (key: keyof PasswordErrors) => FocusEventHandler;
    onToggleTwoFactor: ChangeFunction;
    newPassword: string;
    confirmPassword: string;
    password: string;
    loading: boolean;
    errors: PasswordErrors;
    twoFactorEnabled: boolean;
}

export default function PasswordSettings(props: Props) {
    const {t} = useTranslation();
    const {
        password,
        newPassword,
        confirmPassword,
        onChange,
        onResetError,
        errors,
        loading,
        onSubmit,
        twoFactorEnabled,
        onToggleTwoFactor
    } = props;

    function renderFields() {
        return (
            <div className='fields'>
                <Loader active={loading} />
                <BolmatePasswordInput
                    value={password}
                    onChange={onChange('pass')}
                    label={t('settings.password.currentPassword', 'Huidig wachtwoord *')}
                    error={errors.password}
                    onFocus={onResetError('password')}
                    inputProps={{placeholder: t('settings.password.currentPasswordPlaceholder', 'Huidig wachtwoord')}}
                />
                <BolmatePasswordInput
                    value={newPassword}
                    onChange={onChange('new')}
                    label={t('settings.password.newPassword', 'Nieuw wachtwoord *')}
                    error={errors.newPassword}
                    onFocus={onResetError('newPassword')}
                    inputProps={{placeholder: t('settings.password.newPasswordPlaceholder', 'Nieuw wachtwoord')}}
                />
                <PasswordStrength value={newPassword} />
                <BolmatePasswordInput
                    value={confirmPassword}
                    onChange={onChange('confirm')}
                    label={t('settings.password.confirmPassword', 'Bevestig wachtwoord *')}
                    error={errors.confirmPassword}
                    onFocus={onResetError('confirmPassword')}
                    inputProps={{placeholder: t('settings.password.confirmPasswordPlaceholder', 'Bevestig wachtwoord')}}
                />
            </div>
        );
    }

    function renderPassword() {
        return (
            <div className='change-password-container'>
                <div className='header-container'>
                    <label className='settings-title'>
                        {t('settings.password.changeTitle', 'Wachtwoord wijzigen')}
                    </label>
                    <label className='settings-sub'>
                        {t(
                            'settings.password.changeSub',
                            'We raden sterk aan om iedere 60 dagen je wachtwoord te wijzigen'
                        )}
                    </label>
                </div>
                {renderFields()}
                <BolmateButton onClick={onSubmit} disabled={loading} label={t('settings.password.save', 'Opslaan')} />
            </div>
        );
    }

    function render2Fa() {
        return (
            <div className='two-factor-container'>
                <div className='header-container'>
                    <label className='settings-title'>
                        {t('settings.password.twoFactorTitle', 'Tweestapsverificatie')}
                    </label>
                    <label className='settings-sub'>
                        {t(
                            'settings.password.twoFactorSub',
                            'Met tweestapsverificatie voeg je een extra beveiligingslaag toe aan je account.'
                        )}
                    </label>
                </div>
                <BolmateSwitch
                    value={twoFactorEnabled}
                    onChange={onToggleTwoFactor}
                    mainLabel={t('settings.password.twoFactorTitle', 'Tweestapsverificatie')}
                    labels={[t('settings.password.off', 'UIT'), t('settings.password.on', 'AAN')]}
                />
            </div>
        );
    }

    return (
        <div className='security-settings-page'>
            <InfoBanner
                featureKey='security_settings'
                variant='info'
                title={t('settings.password.bannerTitle', 'Beveiliging')}
                showForNewUsersOnly={true}
            >
                {t(
                    'settings.password.bannerBody',
                    'We raden aan tweestapsverificatie in te schakelen. Je hoeft dan ook je e-mail niet meer te verifieren.'
                )}
            </InfoBanner>

            <Loader active={loading} />

            <div className='security-settings'>
                {renderPassword()}
                {render2Fa()}
            </div>
        </div>
    );
}
